import React, { ButtonHTMLAttributes, useContext } from 'react'
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap'
import styled, { css } from 'styled-components'
import {
  $DCprimaryActiveColor,
  $DCprimaryColor,
  $DCsecondaryColor,
  $DCsecondaryActiveColor,
  $DCSuccessColor,
  $DCWarningColor,
  $DCDangerColor,
  $DCInfoColor,
  $DCLightColor,
  $DCSuccessActiveColor,
  $DCWarningActiveColor,
  $DCDangerActiveColor,
  $DCInfoActiveColor,
  $DCLightActiveColor,
  colors,
} from '../styleGuide'
import './index.scss'

// Create a context to track button nesting
const ButtonContext = React.createContext(false)

export interface DcButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string
  variant?:
    | 'outline-primary'
    | 'outline-secondary'
    | 'outline-success'
    | 'outline-info'
    | 'outline-warning'
    | 'outline-danger'
    | 'outline-light'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'info'
    | 'warning'
    | 'danger'
    | 'light'
    | 'link'
    | 'gradient'
    | string
  size?: 'lg' | 'sm' | 'ex-sm'
  active?: boolean
  disabled?: boolean
  margin?: string
  padding?: string
  tooltip?: string
  children?: React.ReactNode
  background?: string
  style?: React.CSSProperties
  color?: string
  border?: string
  isWrapper?: boolean
  overflow?: string
  zIndex?: number
  cursor?: string
  display?: string
  opacity?: number
  visibility?: string
  transformOrigin?: string
  borderRadius?: string
  boxShadow?: string
  hoverStyle?: React.CSSProperties
  transition?: string
  transform?: string
  animation?: string
  after?: string
  before?: string
  withIcon?: boolean
  width?: string
  alignItems?: string
  justifyContent?: string
}

interface ButtonWrapperProps {
  padding?: string
  color?: string
  style?: React.CSSProperties
  children?: React.ReactNode
  variant?: string
}

const StyledPixelButton = styled(Button)<{ props: DcButtonProps }>`
  &:focus {
    outline: none !important;
    box-shadow: none !important;
  }
  display: ${(props: DcButtonProps) => props.display || 'inline-flex'};
  align-items: ${(props: DcButtonProps) => props.alignItems || 'center'};
  justify-content: ${(props: DcButtonProps) =>
    props.justifyContent || 'center'};
  margin: ${(props: DcButtonProps) => props.margin || '0px'};
  padding: ${(props: DcButtonProps) =>
    props.padding ? props.padding : '9px 18px'} !important;
  width: ${(props: DcButtonProps) =>
    props.width ? props.width + ' !important' : 'auto'};
  font-weight: 400;
  line-height: 1.5rem;
  max-height: 100px;
  border-radius: 0.375rem;
  border: 1px solid ${$DCsecondaryColor};
  color: ${colors.white} !important;

  // Prevent text wrapping to keep button text on single line
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: fit-content;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  background-color: ${$DCsecondaryColor};
  /* &:hover {
    background-color: ${$DCsecondaryActiveColor} !important;
  } */

  a {
    color: ${colors.white};
    text-decoration: none;
    &:hover {
      color: ${colors.white};
    }
  }

  ${(props: DcButtonProps) =>
    props.variant === 'primary' &&
    css`
      background-color: ${$DCprimaryColor} !important;
      border-color: ${$DCprimaryColor} !important;
      font-size: 0.875rem;
      padding: 9px 18px !important;
      color: #fff !important;
      &:hover,
      &:active,
      &:focus {
        background-color: ${$DCprimaryActiveColor} !important;
        border-color: ${$DCprimaryActiveColor} !important;
      }
    `}

  ${(props: DcButtonProps) =>
    props.variant === 'success' &&
    css`
      background-color: ${$DCSuccessColor} !important;
      border-color: ${$DCSuccessColor} !important;

      &:hover,
      &:active,
      &:focus {
        background-color: ${$DCSuccessActiveColor} !important;
        border-color: ${$DCSuccessActiveColor} !important;
      }
    `}

  ${(props: DcButtonProps) =>
    props.variant === 'warning' &&
    css`
      background-color: ${$DCWarningColor} !important;
      border-color: ${$DCWarningColor} !important;

      &:hover,
      &:active,
      &:focus {
        background-color: ${$DCWarningActiveColor} !important;
        border-color: ${$DCWarningActiveColor} !important;
      }
    `}

  ${(props: DcButtonProps) =>
    props.variant === 'gradient' &&
    css`
      background: ${colors.primaryGradient} !important;
      &:hover,
      &:active,
      &:focus {
        background: ${colors.primaryGradient2} !important;
        transform: translateY(-1px);
      }
    `}

  ${(props: DcButtonProps) =>
    props.variant === 'danger' &&
    css`
      background-color: ${$DCDangerColor} !important;
      border-color: ${$DCDangerColor} !important;

      &:hover,
      &:active,
      &:focus {
        background-color: ${$DCDangerActiveColor} !important;
        border-color: ${$DCDangerActiveColor} !important;
      }
    `}

  ${(props: DcButtonProps) =>
    props.variant === 'info' &&
    css`
      background-color: ${$DCInfoColor} !important;
      border-color: ${$DCInfoColor} !important;

      &:hover,
      &:active,
      &:focus {
        background-color: ${$DCInfoActiveColor} !important;
        border-color: ${$DCInfoActiveColor} !important;
      }
    `}

  ${(props: DcButtonProps) =>
    props.variant === 'light' &&
    css`
      background-color: ${$DCLightColor} !important;
      border-color: ${$DCLightColor} !important;
      color: ${colors.black} !important;

      &:hover,
      &:active,
      &:focus {
        background-color: ${$DCLightActiveColor} !important;
        border-color: ${$DCLightActiveColor} !important;
      }
    `}

    ${(props: DcButtonProps) =>
    props.variant === 'link' &&
    css`
      background-color: transparent !important;
      border-color: transparent;
      text-decoration: none;
      color: ${$DCprimaryColor} !important;
      padding: ${(props: DcButtonProps) => props.padding || '0.375rem 0.75rem'};
      &:hover,
      &:hover a,
      &:active,
      &:active a,
      &:focus,
      &:focus a {
        color: ${$DCprimaryActiveColor} !important;
      }
      a {
        color: ${$DCprimaryActiveColor} !important;
      }
    `}

  ${(props: DcButtonProps) =>
    props.variant === 'outline-primary' &&
    css`
      background-color: transparent;
      border-color: ${$DCprimaryColor} !important;
      color: ${$DCprimaryColor} !important;
      outline: none;

      &:hover {
        color: ${colors.white} !important;
        background-color: ${$DCprimaryColor} !important;
      }
    `}
  ${(props: DcButtonProps) =>
    props.variant === 'outline-secondary' &&
    css`
      background-color: transparent;
      border-color: ${$DCsecondaryColor} !important;
      color: ${$DCsecondaryColor} !important;
      outline: none;

      &:hover {
        color: ${colors.white} !important;
        background-color: ${$DCsecondaryActiveColor} !important;
      }
    `}
  ${(props: DcButtonProps) =>
    props.variant === 'outline-success' &&
    css`
      background-color: transparent;
      border-color: ${$DCSuccessColor} !important;
      color: ${$DCSuccessColor} !important;
      outline: none;

      &:hover {
        color: ${colors.white} !important;
        background-color: ${$DCSuccessActiveColor} !important;
        border-color: ${$DCSuccessActiveColor} !important;
      }
    `}
  ${(props: DcButtonProps) =>
    props.variant === 'outline-danger' &&
    css`
      background-color: transparent;
      border-color: ${$DCDangerColor} !important;
      color: ${$DCDangerColor} !important;
      outline: none;

      &:hover {
        color: ${colors.white} !important;
        background-color: ${$DCDangerActiveColor} !important;
        border-color: ${$DCDangerActiveColor} !important;
      }
    `}
  ${(props: DcButtonProps) =>
    props.variant === 'outline-warning' &&
    css`
      background-color: transparent;
      border-color: ${$DCWarningColor} !important;
      color: ${$DCWarningColor} !important;
      outline: none;

      &:hover {
        color: ${colors.white} !important;
        background-color: ${$DCWarningActiveColor} !important;
        border-color: ${$DCWarningActiveColor} !important;
      }
    `}
  ${(props: DcButtonProps) =>
    props.variant === 'outline-info' &&
    css`
      background-color: transparent;
      border-color: ${$DCInfoColor} !important;
      color: ${$DCInfoColor} !important;
      outline: none;

      &:hover {
        color: ${colors.white} !important;
        background-color: ${$DCInfoActiveColor} !important;
        border-color: ${$DCInfoActiveColor} !important;
      }
    `}

    ${(props: DcButtonProps) =>
    props.size == 'lg' &&
    css`
      /* color: ${$DCprimaryColor}; */
      padding: ${(props: DcButtonProps) =>
        props.padding || '15px 22px !important'};
      border-radius: 0.75rem !important;
    `}

    ${(props: DcButtonProps) =>
    props.active &&
    css`
      background-color: ${$DCprimaryActiveColor} !important;
      border-color: ${$DCprimaryActiveColor} !important;
    `}

    ${(props: DcButtonProps) =>
    props.size == 'sm' &&
    css`
      padding: ${(props: DcButtonProps) =>
        props.padding || '2px 12px !important'};
      border-radius: 0.25rem;
      max-height: 30px;
    `}

    ${(props: DcButtonProps) =>
    props.size == 'ex-sm' &&
    css`
      padding: ${(props: DcButtonProps) =>
        props.padding || '1px 8px !important'};
      border-radius: 0.2rem;
      max-height: 24px;
      font-size: 0.75rem;
      line-height: 1.2rem;
    `}

    ${(props: DcButtonProps) =>
    props.active &&
    css`
      background-color: ${$DCprimaryActiveColor} !important;
      border-color: ${$DCprimaryActiveColor} !important;
    `}
    background: ${(props: DcButtonProps) =>
    props.background && `${props.background} !important `};
  color: ${(props: DcButtonProps) =>
    props.color && `${props.color} !important `};
  border: ${(props: DcButtonProps) =>
    props.border && `${props.border} !important `};
  //add more props
  //add more props
  overflow: ${(props: DcButtonProps) => props.overflow || 'visible'};
  z-index: ${(props: DcButtonProps) => props.zIndex || 'auto'};
  cursor: ${(props: DcButtonProps) => props.cursor || 'auto'};
  display: ${(props: DcButtonProps) => props.display || 'block'};
  opacity: ${(props: DcButtonProps) => props.opacity || '1'};
  visibility: ${(props: DcButtonProps) => props.visibility || 'visible'};
  transform-origin: ${(props: DcButtonProps) =>
    props.transformOrigin || 'center'};
  //add more props
  //add more props
  border-radius: ${(props: DcButtonProps) => props.borderRadius || '0.375rem'};
  box-shadow: ${(props: DcButtonProps) => props.boxShadow || 'none'};
  //make the hover !important
  &:hover {
    ${(props: DcButtonProps) =>
      props.hoverStyle &&
      Object.entries(props.hoverStyle)
        .map(([key, value]) => `${key}: ${value} !important;`)
        .join(' ')}
  }
  ::after {
    ${(props: DcButtonProps) => props.after}
  }
  ::before {
    ${(props: DcButtonProps) => props.before}
  }

  //add animation
  transition: ${(props: DcButtonProps) => props.transition};
  transform: ${(props: DcButtonProps) => props.transform};
  animation: ${(props: DcButtonProps) => props.animation};

  // Mobile responsive adjustments
  @media (max-width: 768px) {
    min-width: auto;
    padding: ${(props: DcButtonProps) =>
      props.padding ? props.padding : '8px 16px'} !important;
  }

  @media (max-width: 480px) {
    padding: ${(props: DcButtonProps) =>
      props.padding ? props.padding : '6px 12px'} !important;
    font-size: 0.875rem;
  }

  ${(props: DcButtonProps) =>
    props.withIcon &&
    css`
      display: flex !important;
      align-items: center !important;
      gap: 5px !important;
    `}
  //add style in rest props
    ${(props: DcButtonProps) =>
    props.style &&
    css`
      ${props.style}
    `}
`

const ButtonWrapper = styled.div<ButtonWrapperProps>`
  display: inline-block;
  cursor: pointer;
  color: ${(props) => props.color || $DCprimaryColor};
  text-decoration: none;
  padding: ${(props) => props.padding || '0.375rem 0.75rem'};
  &:hover {
    color: ${$DCprimaryActiveColor};
  }
`

export const DcButton = React.forwardRef<HTMLButtonElement, DcButtonProps>(
  (props, ref) => {
    const {
      className,
      variant = 'primary',
      children,
      active,
      disabled = false,
      margin = '0px',
      tooltip = false,
      background,
      isWrapper = false,
      padding,
      color,
      style,
      withIcon,
      width,
      ...rest
    } = props

    // Check if this button is a child of another button
    const isChildOfButton = useContext(ButtonContext)

    // If this is a child of another button or isWrapper is true, use the wrapper
    if (isChildOfButton || isWrapper) {
      const wrapperProps: ButtonWrapperProps = {
        padding,
        color,
        style,
        children,
        variant,
      }
      return <ButtonWrapper {...wrapperProps} />
    }

    // Wrap children in ButtonContext.Provider to mark them as being inside a button
    const buttonContent = (
      <ButtonContext.Provider value={true}>{children}</ButtonContext.Provider>
    )

    return (
      <React.Fragment>
        {tooltip ? (
          <OverlayTrigger
            placement={'top'}
            overlay={<Tooltip id={`tooltip-${'top'}`}>{tooltip}</Tooltip>}
          >
            <StyledPixelButton
              aria-pressed={active}
              variant={variant}
              background={props.background}
              ref={ref}
              className={className}
              disabled={disabled}
              margin={margin}
              width={width}
              style={style}
              {...rest}
              withIcon={withIcon}
            >
              {buttonContent}
            </StyledPixelButton>
          </OverlayTrigger>
        ) : (
          <StyledPixelButton
            active={active}
            variant={variant}
            background={background}
            color={props.color}
            border={props.border}
            ref={ref}
            className={className}
            disabled={disabled}
            margin={margin}
            withIcon={withIcon}
            width={width}
            {...rest}
          >
            {buttonContent}
          </StyledPixelButton>
        )}
      </React.Fragment>
    )
  },
)

export default DcButton
